import type { CSSProperties } from 'react';

export type SpartanParallaxScrollSpeed =
  | string
  | {
      mobile: string;
      desktop: string;
    };

export type SpartanParallaxMode = 'fold' | 'through';

export const SPARTAN_PRODUCT_FEATURE_PARALLAX_SPEED = {
  mobile: '-0.04',
  desktop: '-0.04',
} as const satisfies SpartanParallaxScrollSpeed;

interface SpartanParallaxStyle extends CSSProperties {
  '--spartan-parallax-mobile-start-y'?: string;
  '--spartan-parallax-mobile-end-y'?: string;
  '--spartan-parallax-desktop-start-y'?: string;
  '--spartan-parallax-desktop-end-y'?: string;
}

const getParallaxOffset = (scrollSpeed: string, direction: 1 | -1) => {
  const speed = Number.parseFloat(scrollSpeed);
  if (!Number.isFinite(speed) || speed === 0) return '0svh';

  return `${speed * direction * 100}svh`;
};

export const resolveSpartanParallaxScrollSpeed = (
  scrollSpeed: SpartanParallaxScrollSpeed | undefined,
  breakpoint: 'mobile' | 'desktop'
): string | undefined => {
  if (scrollSpeed == null) return undefined;
  if (typeof scrollSpeed === 'string') return scrollSpeed;
  return scrollSpeed[breakpoint];
};

export const getSpartanParallaxProps = (
  scrollSpeed: SpartanParallaxScrollSpeed | undefined
): Record<string, string> | undefined =>
  scrollSpeed ? { 'data-spartan-parallax': '' } : undefined;

export const getSpartanParallaxRootProps = (
  mode: SpartanParallaxMode
): Record<string, string> => ({
  'data-spartan-parallax-root': mode,
});

export const getSpartanParallaxStyle = (
  scrollSpeed: SpartanParallaxScrollSpeed | undefined
): SpartanParallaxStyle | undefined => {
  if (!scrollSpeed) return undefined;

  const mobileSpeed = resolveSpartanParallaxScrollSpeed(scrollSpeed, 'mobile');
  const desktopSpeed = resolveSpartanParallaxScrollSpeed(
    scrollSpeed,
    'desktop'
  );
  if (!mobileSpeed || !desktopSpeed) return undefined;

  return {
    '--spartan-parallax-mobile-start-y': getParallaxOffset(mobileSpeed, 1),
    '--spartan-parallax-mobile-end-y': getParallaxOffset(mobileSpeed, -1),
    '--spartan-parallax-desktop-start-y': getParallaxOffset(desktopSpeed, 1),
    '--spartan-parallax-desktop-end-y': getParallaxOffset(desktopSpeed, -1),
  };
};
